import React from 'react';

export default function FindUsSection() {
  // Clean, standard URL for the external link button
  const mapsLink = "https://www.google.com/maps/search/?api=1&query=3787+Transport+St+Suite+A7+Box+%235+Ventura+CA+93003";

  // Clean, bulletproof Embed URL format that Google's servers love
  const mapSrc = "https://maps.google.com/maps?q=3787%20Transport%20St%2C%20Suite%20A7%20Box%20%235%2C%20Ventura%2C%20CA%2093003&t=&z=15&ie=UTF8&iwloc=&output=embed";

  return (
    <section className="w-full bg-white relative overflow-hidden py-28 px-6 border-t border-blue-dark/5">
      {/* Faint vertical grid lines */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(1,46,114,0.02) 0px, rgba(1,46,114,0.02) 1px, transparent 1px, transparent 80px)",
        }}
      />
      
      {/* Ambient background glow behind the map card layout */}
      <div 
        className="absolute bottom-0 right-1/4 w-[600px] h-[350px] rounded-full pointer-events-none filter blur-[150px] opacity-40"
        style={{ backgroundColor: "rgba(1,46,114,0.04)" }}
      />

      <div className="max-w-5xl mx-auto relative">

        {/* Section header */}
        <div className="flex flex-col items-center gap-5 mb-16 text-center">
          <div className="flex items-center gap-3">
            <span
              className="font-cinzel text-blue-dark uppercase tracking-widest font-medium"
              style={{ fontSize: "11px", letterSpacing: "0.25em" }}
            >
              — Our Location —
            </span>
          </div>

          <h2
            className="font-cinzel font-bold text-blue-dark uppercase"
            style={{
              fontSize: "clamp(26px, 4vw, 42px)",
              letterSpacing: "0.04em",
              lineHeight: "1.2",
            }}
          >
            Find Us
          </h2>

          <div className="flex items-center gap-4">
            <div className="h-px w-16 bg-blue-dark opacity-20" />
            <div className="w-1 h-1 rotate-45 bg-blue-dark opacity-40" />
            <div className="h-px w-16 bg-blue-dark opacity-20" />
          </div>
        </div>

        {/* Main card */}
        <div
          className="rounded-2xl overflow-hidden bg-white relative"
          style={{
            border: "1px solid rgba(1,46,114,0.08)",
            boxShadow: "0 20px 50px rgba(1,46,114,0.05)",
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-5">

            {/* LEFT — info panel */}
            <div
              className="lg:col-span-2 flex flex-col justify-between p-10 lg:p-12 gap-10 bg-white relative"
              style={{ borderRight: "1px solid rgba(1,46,114,0.08)" }}
            >
              <div className="flex flex-col gap-8">

                {/* Address */}
                <div className="flex flex-col gap-3.5 group">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 group-hover:bg-blue-dark/10"
                      style={{ backgroundColor: "rgba(1,46,114,0.05)" }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#012E72" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-80">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <span className="font-cinzel text-blue-dark uppercase tracking-wider font-semibold opacity-75" style={{ fontSize: "10px", letterSpacing: "0.15em" }}>
                      Address
                    </span>
                  </div>
                  <p className="font-constantia text-black-deep leading-relaxed pl-0.5" style={{ fontSize: "15.5px", opacity: 0.8 }}>
                    3787 Transport ST<br />
                    <span className="opacity-60 text-[0.95em]">Suite A7 Box #5</span><br />
                    Ventura, CA 93003
                  </p>
                </div>

                <div className="h-px w-full bg-blue-dark opacity-10" />

                {/* Office Hours */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "rgba(1,46,114,0.05)" }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#012E72" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-80">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <span className="font-cinzel text-blue-dark uppercase tracking-wider font-semibold opacity-75" style={{ fontSize: "10px", letterSpacing: "0.15em" }}>
                      Office Hours
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 pl-0.5 w-full">
                    {[
                      { day: "Mon – Fri", hours: "9:00 AM – 5:00 PM" },
                      { day: "Saturday", hours: "By Appointment" },
                      { day: "Sunday", hours: "Closed", isClosed: true },
                    ].map(({ day, hours, isClosed }) => (
                      <div key={day} className="flex justify-between items-center gap-4 text-[13.5px]">
                        <span className="font-constantia text-black-deep opacity-50">{day}</span>
                        <span className={`font-constantia ${isClosed ? 'text-black-deep opacity-40 italic' : 'text-blue-dark font-medium opacity-85'}`}>{hours}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="h-px w-full bg-blue-dark opacity-10" />

                {/* Phone */}
                <div className="flex flex-col gap-3.5 group">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 group-hover:bg-blue-dark/10"
                      style={{ backgroundColor: "rgba(1,46,114,0.05)" }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#012E72" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-80">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <span className="font-cinzel text-blue-dark uppercase tracking-wider font-semibold opacity-75" style={{ fontSize: "10px", letterSpacing: "0.15em" }}>
                      Phone
                    </span>
                  </div>
                  <a
                    href="tel:+18054951000"
                    className="font-constantia text-blue-dark font-medium transition-colors duration-300 hover:text-black-deep pl-0.5"
                    style={{ fontSize: "16px" }}
                  >
                    (805) 495-1000
                  </a>
                </div>
              </div>

              {/* Google Maps link */}
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 font-cinzel text-blue-dark uppercase tracking-widest transition-all duration-200 group mt-4 pl-0.5"
                style={{ fontSize: "11px", letterSpacing: "0.15em" }}
              >
                <span className="border-b border-blue-dark/30 group-hover:border-blue-dark transition-all duration-300 pb-0.5 font-medium">
                  Open in Google Maps
                </span>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="group-hover:translate-x-1 transition-transform duration-300"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>

            {/* RIGHT — map iframe container */}
            <div className="lg:col-span-3 relative bg-white-warm" style={{ minHeight: "460px" }}>
              <iframe
                title="Paladin Location"
                src={mapSrc}
                width="100%"
                height="100%"
                style={{ border: 0, display: "block", minHeight: "460px" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}